/**
 * src/components/dashboard/StockReservationPanel.tsx
 * Full-width panel for the STOCK_RESERVATION workflow state.
 * Two-column layout: left 60% stock hold status, right 40% reservation summary.
 * Gates FINALIZE transition until all lines are HELD or SUBSTITUTED.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReservationLineItem, ReservationStatus, StockReservation, SubstituteCandidate } from '@/lib/types/workflow';
import { confirmSubstitution, confirmHold, getSubstituteCandidates } from '@/lib/services/reservations';
import { useToast } from '@/context/ToastContext';
import { formatRand } from '@/lib/utils/currency';
import { formatSAST } from '@/lib/utils/date';
import { brand, fonts, motion, styles } from '@/lib/tokens';

type Props = {
  reservation: StockReservation;
  bookingId: string;
  /** Current operator's id — sent to POST /reservations/{id}/confirm-hold. */
  actorId: string;
  /** Fired after an accepted AI substitute or a manually-entered substitute
   *  changes a line's name/price — not fired for a declined substitute,
   *  since nothing about the quoted item changes there. Passes every
   *  current line so the caller can re-sync the draft in one call. */
  onSubstitutionConfirmed: (lines: ReservationLineItem[]) => void;
  /** State of the draft re-sync that follows a substitution decision. The
   *  hold can't be confirmed (and so the quote can't move on to Finalize &
   *  Send) until the regenerated draft has actually been saved. */
  draftSyncStatus: 'idle' | 'syncing' | 'failed';
  onRetryDraftSync: () => void;
  onAllHeld: () => void;
  /** Async now (was `() => void`) — see isCancelling below for why. */
  onCancel: () => Promise<void>;
};

export function StockReservationPanel({
  reservation,
  bookingId,
  actorId,
  onSubstitutionConfirmed,
  draftSyncStatus,
  onRetryDraftSync,
  onAllHeld,
  onCancel,
}: Props) {
  const toast = useToast();
  const [lines, setLines] = useState<ReservationLineItem[]>(reservation.lines);
  // Always-current copy of `lines`. Substitution handlers below used to read
  // the new list back out of a setLines() updater callback — but React runs
  // updaters lazily (on the next render), so the value was still the OLD
  // list when it was handed to onSubstitutionConfirmed(). The quote re-sync
  // then ran against the un-substituted lines and the draft never changed
  // (reported live 30 Oct 2026: substitution added in Stock Check, old items
  // on Finalize, old items in the sent email). Compute from the ref instead.
  const linesRef = useRef<ReservationLineItem[]>(reservation.lines);
  linesRef.current = lines;
  function applyUpdatedLine(updated: ReservationLineItem, lineItemId: string): ReservationLineItem[] {
    const nextLines = linesRef.current.map((l) => (l.gearItemId === lineItemId ? updated : l));
    linesRef.current = nextLines;
    setLines(nextLines);
    return nextLines;
  }
  const [confirming, setConfirming] = useState<string | null>(null);
  const [holdReference, setHoldReference] = useState('');
  const [confirmingHold, setConfirmingHold] = useState(false);
  // Real fix, 29 Sep 2026: "Cancel Reservation" was the only action button
  // on this panel with no disabled/in-flight guard at all (every other
  // button here — Confirm Manual Hold, the substitute-decision buttons —
  // disables itself via confirming/confirmingHold while its call is in
  // flight). onCancel (DashboardPage's goCancelReservation) awaits two
  // sequential network calls (release the inventory-service reservation,
  // then revert the workflow status), so a second click during that window
  // fired it again — the second revertToPendingApproval call then hit the
  // workflow already at PENDING_APPROVAL, which isn't a valid transition
  // from itself, and failed. Depending on timing that could leave things in
  // an inconsistent state that a later Reject then 422'd against. Reported
  // live as "the fix only worked for one email" — this and the
  // DashboardPage-side fixes together close that off. See
  // [[finding-cancel-reservation-does-not-revert-workflow-status]].
  const [isCancelling, setIsCancelling] = useState(false);

  async function handleCancel() {
    if (isCancelling) return;
    setIsCancelling(true);
    try {
      await onCancel();
    } finally {
      setIsCancelling(false);
    }
  }

  // Matches inventory-service's confirm-hold gate exactly (routes/reservations.py
  // pending_decision): a CONFLICT line only blocks progress while it still has a
  // substitute the operator hasn't accepted or declined yet. A CONFLICT line with
  // no substitute at all (substituteAvailable === false) has nothing left to
  // decide — that gear simply can't be fulfilled and needs manual outsourcing,
  // but it must not permanently block confirming the hold for the other lines.
  const isLineBlocking = (l: ReservationLineItem) =>
    l.reservationStatus === 'CONFLICT' && l.substituteAvailable;
  const allResolved = !lines.some(isLineBlocking);
  const draftReady = draftSyncStatus === 'idle';
  const canConfirmHold = allResolved && holdReference.trim().length > 0 && !confirmingHold && draftReady;

  async function handleAcceptSubstitute(lineItemId: string) {
    setConfirming(lineItemId);
    try {
      const updated = await confirmSubstitution(reservation.id, lineItemId, true);
      onSubstitutionConfirmed(applyUpdatedLine(updated, lineItemId));
    } finally {
      setConfirming(null);
    }
  }

  // For a CONFLICT line with no AI-suggested substitute at all — lets the
  // operator type in their own stand-in item rather than leaving it
  // permanently unresolved. See [[finding-manual-substitute-entry]].
  async function handleManualSubstitute(lineItemId: string, name: string, unitPrice: number) {
    setConfirming(lineItemId);
    try {
      const updated = await confirmSubstitution(reservation.id, lineItemId, true, { name, unitPrice });
      onSubstitutionConfirmed(applyUpdatedLine(updated, lineItemId));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to save the manual substitute';
      toast.error(msg);
    } finally {
      setConfirming(null);
    }
  }

  async function handleDeclineSubstitute(lineItemId: string) {
    setConfirming(lineItemId);
    try {
      const updated = await confirmSubstitution(reservation.id, lineItemId, false);
      applyUpdatedLine(updated, lineItemId);
    } finally {
      setConfirming(null);
    }
  }

  async function handleConfirmHold() {
    if (!canConfirmHold) return;
    setConfirmingHold(true);
    try {
      await confirmHold(reservation.id, actorId, holdReference.trim());
      onAllHeld();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to confirm the stock hold';
      toast.error(msg);
    } finally {
      setConfirmingHold(false);
    }
  }

  const subtotal = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
  const vat = subtotal * 0.15;
  const total = subtotal + vat;

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        padding: 24,
        gap: 24,
      }}
    >
      {/* Left column — 60% */}
      <div
        style={{
          flex: '0 0 60%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="panel-header" style={{ margin: '-24px -24px 16px', paddingLeft: 0, background: 'transparent', borderBottom: 'none' }}>
          <span style={{ ...styles.monoLabel, fontSize: 11, letterSpacing: '0.1em' }}>
            Stock Check Status
          </span>
        </div>

        {/* GLOW never places or modifies a Current RMS hold itself — this is
            always a manual operator action. Kept short and above the fold
            so it's read before the line list, not just implied by the
            per-line copy below. */}
        <p
          style={{
            ...styles.monoLabel,
            letterSpacing: '0.02em',
            textTransform: 'none',
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          GLOW does not place or modify holds in Current RMS. Check each item
          below in Current RMS yourself, place any hold there, then record
          the result here.
        </p>

        {/* Countdown */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <span style={{ ...styles.monoLabel, fontSize: 11, letterSpacing: '0.1em' }}>
            Hold Expires
          </span>
          <Countdown expiresAt={reservation.expiresAt} />
        </div>

        {/* Line cards */}
        <div
          className="custom-scroll"
          style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {lines.map((line) => (
            <ReservationLineCard
              key={line.gearItemId}
              reservationId={reservation.id}
              line={line}
              isConfirming={confirming === line.gearItemId}
              onAcceptSubstitute={() => handleAcceptSubstitute(line.gearItemId)}
              onDeclineSubstitute={() => handleDeclineSubstitute(line.gearItemId)}
              onManualSubstitute={(name, unitPrice) => handleManualSubstitute(line.gearItemId, name, unitPrice)}
            />
          ))}
        </div>
      </div>

      {/* Right column — 40% */}
      <div
        style={{
          flex: '0 0 40%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <span style={{ ...styles.monoLabel, fontSize: 11, letterSpacing: '0.1em' }}>
            Reservation Summary
          </span>
        </div>

        <div
          className="glass-card custom-scroll"
          style={{
            borderRadius: 12,
            padding: 24,
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {/* Check Current RMS — opens the account's Current RMS web app in
              a new tab so the operator can look up live availability and
              place a hold there. Generic link only (never a per-item deep
              link — see StockReservation.currentRmsWebUrl's docstring).
              Hidden entirely if inventory-service has no subdomain
              configured, rather than rendering a dead/guessed link. */}
          {reservation.currentRmsWebUrl && (
            <a
              href={reservation.currentRmsWebUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 16,
                padding: '8px 14px',
                borderRadius: 9999,
                border: `1px solid ${brand.hairline}`,
                color: brand.onSurface,
                fontFamily: fonts.sans,
                fontSize: 13,
                textDecoration: 'none',
              }}
            >
              Check Current RMS ↗
            </a>
          )}

          {/* RMS Hold Reference — operator-entered after manually placing the
              hold in Current RMS's own UI (manual-hold workaround; Current
              RMS's own hold-placement API is broken — see Finding 3). This
              value is purely informational: GLOW never uses it to create or
              modify anything in Current RMS. */}
          <div style={{ marginBottom: 8 }}>
            <span style={{ ...styles.monoLabel, letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              RMS Hold Reference — record of what you placed manually
            </span>
            <input
              value={holdReference}
              onChange={(e) => setHoldReference(e.target.value)}
              disabled={!allResolved || confirmingHold}
              placeholder={allResolved ? 'Enter after placing the hold in Current RMS' : 'Resolve all lines first'}
              style={{
                width: '100%',
                background: brand.surfaceSubtle,
                border: `1px solid ${brand.hairlineMid}`,
                color: brand.onSurface,
                padding: '8px 10px',
                borderRadius: 8,
                fontFamily: fonts.mono,
                fontSize: 12,
                opacity: allResolved ? 1 : 0.5,
              }}
            />
          </div>
          <MetaRow label="Booking" value={bookingId} />
          <MetaRow label="Reserved At" value={formatSAST(reservation.reservedAt)} />
          <MetaRow label="Hold Expires" value={formatSAST(reservation.expiresAt)} />

          <div style={{ borderTop: `1px solid ${brand.hairlineSoft}`, margin: '16px 0' }} />

          {/* Pricing */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <PricingRow label="Subtotal" value={subtotal} />
            <PricingRow label="VAT (15%)" value={vat} muted />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontFamily: fonts.sans, fontSize: 13, color: brand.onSurface, fontWeight: 600 }}>
                Total
              </span>
              <span style={{ fontFamily: fonts.sans, fontSize: 16, color: brand.onSurface, fontWeight: 600 }}>
                {formatRand(total)}
              </span>
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${brand.hairlineSoft}`, margin: '16px 0' }} />

          {/* Draft re-sync status — Confirm Manual Hold stays disabled until
              the regenerated quote draft has been saved, so a stale draft can
              never be carried through to Finalize & Send. */}
          {draftSyncStatus === 'syncing' && (
            <div
              role="status"
              style={{ fontFamily: fonts.sans, fontSize: 12, color: brand.onSurface, opacity: 0.7, marginBottom: 12 }}
            >
              Updating the quote draft with your stock decisions…
            </div>
          )}
          {draftSyncStatus === 'failed' && (
            <div
              role="alert"
              style={{ fontFamily: fonts.sans, fontSize: 12, color: brand.onSurface, marginBottom: 12 }}
            >
              The quote draft could not be updated.{' '}
              <button
                onClick={onRetryDraftSync}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: brand.onSurface,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              onClick={handleConfirmHold}
              disabled={!canConfirmHold}
              style={{
                width: '100%',
                padding: '10px 20px',
                borderRadius: 9999,
                background: canConfirmHold ? brand.onSurface : 'rgba(229,226,225,0.3)',
                color: brand.canvas,
                border: 'none',
                fontFamily: fonts.sans,
                fontSize: 13,
                fontWeight: 600,
                cursor: canConfirmHold ? 'pointer' : 'not-allowed',
                opacity: canConfirmHold ? 1 : 0.5,
                transition: `all ${motion.default}`,
              }}
            >
              {confirmingHold ? 'Recording…' : 'Confirm Manual Hold'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isCancelling}
              style={{
                width: '100%',
                padding: '10px 20px',
                borderRadius: 9999,
                background: 'transparent',
                color: brand.onSurface,
                border: `1px solid ${brand.hairline}`,
                fontFamily: fonts.sans,
                fontSize: 13,
                cursor: isCancelling ? 'not-allowed' : 'pointer',
                opacity: isCancelling ? 0.5 : 1,
                transition: `background ${motion.default}`,
              }}
            >
              {isCancelling ? 'Cancelling…' : 'Cancel Reservation'}
            </button>
          </div>

          {/* Caption */}
          <p
            style={{
              ...styles.monoLabel,
              textAlign: 'center',
              marginTop: 12,
              lineHeight: 1.5,
            }}
          >
            See the countdown above for when this hold expires. Releasing this
            reservation requires re-checking availability.
          </p>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

/**
 * Lenient parse of an operator-typed day rate: "75", "75.50", "75,50",
 * "R75", "R 1 250/day" and "75 per day" all work. Returns 0 (invalid) when
 * no number can be found. Before this the field used Number(), so anything
 * with a unit ("75/day") became NaN and the Use This Substitute button
 * stayed disabled with no explanation.
 */
function parseRandAmount(raw: string): number {
  const match = raw.replace(/\u00a0/g, ' ').match(/\d[\d ]*(?:[.,]\d{1,2})?/);
  if (!match) return 0;
  const value = Number(match[0].replace(/ /g, '').replace(',', '.'));
  return Number.isFinite(value) ? value : 0;
}

function Countdown({ expiresAt }: { expiresAt: string }) {
  const [remaining, setRemaining] = useState('--:--:--');
  const [colorVal, setColorVal] = useState<string>(brand.success);

  useEffect(() => {
    function tick() {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setRemaining('00:00:00');
        setColorVal(brand.error);
        return;
      }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setRemaining(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`,
      );
      setColorVal(h >= 4 ? brand.success : h >= 1 ? brand.accent : brand.error);
    }
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return (
    <span
      style={{
        fontFamily: fonts.mono,
        fontSize: 16,
        color: colorVal,
        letterSpacing: '0.06em',
      }}
    >
      {remaining}
    </span>
  );
}

function ReservationLineCard({
  reservationId,
  line,
  isConfirming,
  onAcceptSubstitute,
  onDeclineSubstitute,
  onManualSubstitute,
}: {
  reservationId: string;
  line: ReservationLineItem;
  isConfirming: boolean;
  onAcceptSubstitute: () => void;
  onDeclineSubstitute: () => void;
  onManualSubstitute: (name: string, unitPrice: number) => void;
}) {
  const isConflict  = line.reservationStatus === 'CONFLICT';
  const isResolved  = line.reservationStatus === 'HELD' || line.reservationStatus === 'SUBSTITUTED';

  // Two independent, operator-triggered ways to populate the dropdown
  // below (design decision, 28 Sep 2026 — see
  // [[finding-substitute-dropdown-combined-search-and-browse]]): typed
  // free-text search across the entire catalogue, and a "Browse
  // {category} items" toggle the operator clicks to filter to this
  // line's own stored category. Neither fires on its own — nothing loads
  // until the operator types or clicks. This builds on the 18 Sep 2026
  // decision ([[finding-substitute-dropdown-manual-search-not-automatic]])
  // that removed an earlier version which auto-loaded a category-filtered
  // list the moment a line went into CONFLICT; a deliberate operator
  // click is a different, still-human-driven action from that automatic
  // load, so bringing category filtering back as an explicit toggle
  // doesn't reintroduce the problem. Free-text entry (further below)
  // remains the fallback for an outsourced / non-catalog item, or when
  // neither the search nor the browse toggle turns up anything.
  const [searchQuery, setSearchQuery] = useState('');
  const [browseCategory, setBrowseCategory] = useState(false);
  const [searchResults, setSearchResults] = useState<SubstituteCandidate[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');

  const [manualOpen, setManualOpen] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualPrice, setManualPrice] = useState('');

  const needsSubstituteChoice = isConflict && !line.substituteAvailable;

  // Debounced search — fires once the operator has typed at least 2
  // characters and paused for 300ms, OR has the "Browse {category}"
  // toggle on (in which case an empty query is fine — the category
  // filter alone is enough of an operator-driven request). Nothing calls
  // the backend on mount, or while the query is short AND the toggle is
  // off.
  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2 && !browseCategory) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      getSubstituteCandidates(reservationId, line.gearItemId, query, browseCategory)
        .then((result) => {
          if (!cancelled) setSearchResults(result);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, browseCategory, reservationId, line.gearItemId]);

  // Manual-entry name suggestions (30 Oct 2026): while the operator types a
  // substitute name by hand, offer matching catalogue items as browser
  // autocomplete suggestions (a <datalist>) — still operator-typed, never
  // automatic: nothing is requested until they've typed 2+ characters.
  // Picking a suggestion that matches a catalogue item also pre-fills its
  // day rate if the price box is still empty.
  const [manualSuggestions, setManualSuggestions] = useState<SubstituteCandidate[]>([]);
  useEffect(() => {
    const query = manualName.trim();
    if (!manualOpen || query.length < 2) {
      setManualSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      getSubstituteCandidates(reservationId, line.gearItemId, query, false)
        .then((result) => {
          if (!cancelled) setManualSuggestions(result.slice(0, 15));
        })
        .catch(() => {
          if (!cancelled) setManualSuggestions([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [manualName, manualOpen, reservationId, line.gearItemId]);

  function handleManualNameChange(value: string) {
    setManualName(value);
    const match = manualSuggestions.find((c) => c.name.toLowerCase() === value.trim().toLowerCase());
    if (match && match.dayRate != null && manualPrice.trim() === '') {
      setManualPrice(String(match.dayRate));
    }
  }

  const selectedPriceNum = parseRandAmount(selectedPrice);
  const selectedValid = selectedCandidateId.length > 0 && selectedPriceNum > 0;

  function handleCandidateChange(candidateId: string) {
    setSelectedCandidateId(candidateId);
    const candidate = (searchResults ?? []).find((c) => c.id === candidateId);
    setSelectedPrice(candidate?.dayRate != null ? String(candidate.dayRate) : '');
  }

  function submitCandidate() {
    if (!selectedValid) return;
    const candidate = (searchResults ?? []).find((c) => c.id === selectedCandidateId);
    if (!candidate) return;
    onManualSubstitute(candidate.name, selectedPriceNum);
  }

  const manualPriceNum = parseRandAmount(manualPrice);
  const manualValid = manualName.trim().length > 0 && manualPriceNum > 0;

  function submitManual() {
    if (!manualValid) return;
    onManualSubstitute(manualName.trim(), manualPriceNum);
  }

  return (
    <div
      className="card-border"
      style={{ padding: 16, borderRadius: 12, background: brand.surface2 }}
    >
      {/* Row 1 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{ fontFamily: fonts.sans, fontSize: 14, fontWeight: 600, color: brand.onSurface }}
          >
            {line.name}
          </span>
          <span
            style={{
              fontFamily:   fonts.mono,
              fontSize:     10,
              color:        brand.monoDim,
              background:   brand.hairlineSoft,
              border:       `1px solid ${brand.hairline}`,
              borderRadius: 9999,
              padding:      '2px 8px',
            }}
          >
            ×{line.quantity}
          </span>
        </div>
        <LineStatusChip status={line.reservationStatus} substituteAvailable={line.substituteAvailable} />
      </div>

      {/* Row 2 — conflict panel */}
      {isConflict && line.substituteAvailable && (
        <div
          style={{
            background:   brand.accentTint,
            border:       `1px solid rgba(104, 187, 203,0.15)`,
            borderRadius: 8,
            padding:      12,
            marginTop:    8,
          }}
        >
          <p style={{ fontFamily: fonts.sans, fontSize: 13, color: brand.muted, marginBottom: 4 }}>
            Original unavailable.
          </p>
          <p style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, color: brand.accent, marginBottom: 8 }}>
            Substitute: {line.substituteName}
            {line.substituteUnitPrice != null && ` — ${formatRand(line.substituteUnitPrice)}/day`}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onAcceptSubstitute}
              disabled={isConfirming}
              style={{
                padding:      '6px 12px',
                borderRadius: 8,
                background:   'transparent',
                border:       `1px solid rgba(104, 187, 203,0.4)`,
                color:        brand.accent,
                fontFamily:   fonts.sans,
                fontSize:     12,
                cursor:       isConfirming ? 'wait' : 'pointer',
                opacity:      isConfirming ? 0.5 : 1,
              }}
            >
              {isConfirming ? 'Confirming…' : 'Accept Substitute'}
            </button>
            <button
              onClick={onDeclineSubstitute}
              disabled={isConfirming}
              style={{
                padding:      '6px 12px',
                borderRadius: 8,
                background:   'transparent',
                border:       `1px solid ${brand.hairline}`,
                color:        brand.onSurfaceVariant,
                fontFamily:   fonts.sans,
                fontSize:     12,
                cursor:       isConfirming ? 'wait' : 'pointer',
                opacity:      isConfirming ? 0.5 : 1,
              }}
            >
              Keep Original
            </button>
          </div>
        </div>
      )}

      {/* Row 2b — conflict, no AI-suggested substitute: doesn't block
          confirming the hold for the other lines (see isLineBlocking in the
          parent panel), but the operator can type in their own stand-in
          item instead of just leaving it for manual outsourcing — see
          [[finding-manual-substitute-entry]]. */}
      {needsSubstituteChoice && (
        <div
          style={{
            background:   brand.errorBgSoft,
            border:       `1px solid ${brand.errorBorder}`,
            borderRadius: 8,
            padding:      12,
            marginTop:    8,
          }}
        >
          <p style={{ fontFamily: fonts.sans, fontSize: 13, color: brand.error, marginBottom: 8 }}>
            No AI-suggested substitute for this line. It won't block
            confirming the hold for the rest of this booking.
          </p>

          {!manualOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ ...styles.monoLabel, letterSpacing: '0.02em', textTransform: 'none' }}>
                Search Current RMS for a substitute yourself — nothing is
                suggested automatically.
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedCandidateId('');
                    setSelectedPrice('');
                  }}
                  placeholder="Search the catalog by name…"
                  disabled={isConfirming}
                  style={{
                    flex: '1 1 200px',
                    background: brand.surfaceSubtle,
                    border: `1px solid ${brand.hairlineMid}`,
                    color: brand.onSurface,
                    padding: '6px 10px',
                    borderRadius: 8,
                    fontFamily: fonts.sans,
                    fontSize: 12,
                  }}
                />
                {/* Explicit, operator-clicked category filter — a shortcut
                    for "show me what's already in this line's category"
                    that sits alongside free-text search rather than
                    replacing it. Only shown when the line actually has a
                    stored category (some lines never matched anything —
                    see needsSubstituteChoice's docstring). Never fires on
                    its own; the operator has to click it. */}
                {line.category && (
                  <button
                    type="button"
                    onClick={() => {
                      setBrowseCategory((prev) => !prev);
                      setSelectedCandidateId('');
                      setSelectedPrice('');
                    }}
                    disabled={isConfirming}
                    style={{
                      flex: '0 0 auto',
                      padding: '6px 12px',
                      borderRadius: 8,
                      background: browseCategory ? brand.accentTint : 'transparent',
                      border: `1px solid ${browseCategory ? 'rgba(104, 187, 203,0.4)' : brand.hairlineMid}`,
                      color: browseCategory ? brand.accent : brand.onSurfaceVariant,
                      fontFamily: fonts.sans,
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      cursor: isConfirming ? 'wait' : 'pointer',
                    }}
                  >
                    {browseCategory ? `✓ Browsing ${line.category}` : `Browse ${line.category} items`}
                  </button>
                )}
              </div>

              {searching && (
                <p style={{ ...styles.monoLabel, letterSpacing: '0.02em', textTransform: 'none' }}>
                  Searching…
                </p>
              )}

              {!searching && searchResults !== null && searchResults.length === 0 && (
                <p style={{ ...styles.monoLabel, letterSpacing: '0.02em', textTransform: 'none' }}>
                  No matches. Try a different search, or enter a substitute manually below.
                </p>
              )}

              {!searching && (searchResults?.length ?? 0) > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    value={selectedCandidateId}
                    onChange={(e) => handleCandidateChange(e.target.value)}
                    disabled={isConfirming}
                    style={{
                      flex: '1 1 200px',
                      background: brand.surfaceSubtle,
                      border: `1px solid ${brand.hairlineMid}`,
                      color: brand.onSurface,
                      padding: '6px 10px',
                      borderRadius: 8,
                      fontFamily: fonts.sans,
                      fontSize: 12,
                    }}
                  >
                    <option value="">Choose from search results…</option>
                    {searchResults!.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.dayRate != null ? ` — ${formatRand(c.dayRate)}/day` : ''}
                      </option>
                    ))}
                  </select>
                  <input
                    value={selectedPrice}
                    onChange={(e) => setSelectedPrice(e.target.value)}
                    placeholder="Rand/day"
                    inputMode="decimal"
                    disabled={isConfirming || !selectedCandidateId}
                    style={{
                      flex: '0 0 90px',
                      background: brand.surfaceSubtle,
                      border: `1px solid ${brand.hairlineMid}`,
                      color: brand.onSurface,
                      padding: '6px 10px',
                      borderRadius: 8,
                      fontFamily: fonts.mono,
                      fontSize: 12,
                      opacity: selectedCandidateId ? 1 : 0.5,
                    }}
                  />
                  <button
                    onClick={submitCandidate}
                    disabled={isConfirming || !selectedValid}
                    style={{
                      padding:      '6px 12px',
                      borderRadius: 8,
                      background:   'transparent',
                      border:       `1px solid rgba(104, 187, 203,0.4)`,
                      color:        brand.accent,
                      fontFamily:   fonts.sans,
                      fontSize:     12,
                      cursor:       isConfirming || !selectedValid ? 'not-allowed' : 'pointer',
                      opacity:      isConfirming || !selectedValid ? 0.5 : 1,
                    }}
                  >
                    {isConfirming ? 'Saving…' : 'Use This Substitute'}
                  </button>
                </div>
              )}

              <button
                onClick={() => setManualOpen(true)}
                disabled={isConfirming}
                style={{
                  alignSelf: 'flex-start',
                  padding:      0,
                  background:   'transparent',
                  border:       'none',
                  color:        brand.onSurfaceVariant,
                  fontFamily:   fonts.sans,
                  fontSize:     11,
                  textDecoration: 'underline',
                  cursor:       isConfirming ? 'wait' : 'pointer',
                }}
              >
                Enter a substitute manually instead
              </button>
            </div>
          )}

          {manualOpen && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={manualName}
                onChange={(e) => handleManualNameChange(e.target.value)}
                list={`manual-sub-${line.gearItemId}`}
                placeholder="Substitute item name"
                disabled={isConfirming}
                style={{
                  flex: '1 1 160px',
                  background: brand.surfaceSubtle,
                  border: `1px solid ${brand.hairlineMid}`,
                  color: brand.onSurface,
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontFamily: fonts.sans,
                  fontSize: 12,
                }}
              />
              <datalist id={`manual-sub-${line.gearItemId}`}>
                {manualSuggestions.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.dayRate != null ? `${formatRand(c.dayRate)}/day` : ''}
                  </option>
                ))}
              </datalist>
              <input
                value={manualPrice}
                onChange={(e) => setManualPrice(e.target.value)}
                placeholder="Rand/day"
                inputMode="decimal"
                disabled={isConfirming}
                style={{
                  flex: '0 0 90px',
                  background: brand.surfaceSubtle,
                  border: `1px solid ${brand.hairlineMid}`,
                  color: brand.onSurface,
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontFamily: fonts.mono,
                  fontSize: 12,
                }}
              />
              <button
                onClick={submitManual}
                disabled={isConfirming || !manualValid}
                style={{
                  padding:      '6px 12px',
                  borderRadius: 8,
                  background:   'transparent',
                  border:       `1px solid rgba(104, 187, 203,0.4)`,
                  color:        brand.accent,
                  fontFamily:   fonts.sans,
                  fontSize:     12,
                  cursor:       isConfirming || !manualValid ? 'not-allowed' : 'pointer',
                  opacity:      isConfirming || !manualValid ? 0.5 : 1,
                }}
              >
                {isConfirming ? 'Saving…' : 'Use This Substitute'}
              </button>
              {manualPrice.trim() !== '' && !(manualPriceNum > 0) && (
                <span style={{ ...styles.monoLabel, letterSpacing: '0.02em', textTransform: 'none', flexBasis: '100%' }}>
                  Enter the price per day as a number, e.g. 75
                </span>
              )}
              {(searchResults?.length ?? 0) > 0 && (
                <button
                  onClick={() => setManualOpen(false)}
                  disabled={isConfirming}
                  style={{
                    padding:      '6px 12px',
                    borderRadius: 8,
                    background:   'transparent',
                    border:       'none',
                    color:        brand.onSurfaceVariant,
                    fontFamily:   fonts.sans,
                    fontSize:     12,
                    cursor:       isConfirming ? 'wait' : 'pointer',
                  }}
                >
                  Back to catalog
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Row 3 — resolved indicator */}
      {isResolved && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: brand.success }}>
            check_circle
          </span>
        </div>
      )}
    </div>
  );
}

function LineStatusChip({ status, substituteAvailable }: { status: ReservationStatus; substituteAvailable: boolean }) {
  // Labels intentionally avoid "Held"/"Reserved" language for anything
  // GLOW itself computed — HELD here means "available per GLOW's live
  // Current RMS stock check," not "a hold exists in Current RMS." The
  // operator still has to check Current RMS and place any hold manually;
  // see the panel's top-of-column instruction and per-line copy below.
  switch (status) {
    case 'HELD':
      return <span className="chip-held">Available</span>;
    case 'PENDING':
      return (
        <span
          style={{
            background:    brand.hairlineSoft,
            border:        `1px solid ${brand.hairline}`,
            color:         'rgba(var(--fg-rgb),0.5)',
            padding:       '2px 8px',
            borderRadius:  4,
            fontFamily:    fonts.mono,
            fontSize:      9,
            textTransform: 'uppercase',
          }}
        >
          Not Checked
        </span>
      );
    case 'CONFLICT':
      return (
        <span className="chip-unavailable">
          {substituteAvailable ? 'Substitution Required' : 'Unavailable'}
        </span>
      );
    case 'SUBSTITUTED':
      return <span className="chip-in-stock">Substitution Confirmed</span>;
  }
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ ...styles.monoLabel, letterSpacing: '0.06em' }}>
        {label}
      </span>
      <span style={{ fontFamily: fonts.mono, fontSize: 11, color: brand.onSurface }}>
        {value}
      </span>
    </div>
  );
}

function PricingRow({ label, value, muted = false }: { label: string; value: number; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontFamily: fonts.sans, fontSize: 13, color: muted ? brand.muted : brand.onSurfaceVariant }}>
        {label}
      </span>
      <span style={{ fontFamily: fonts.sans, fontSize: 13, color: muted ? brand.muted : brand.onSurfaceVariant }}>
        {formatRand(value)}
      </span>
    </div>
  );
}
